import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Gauge, TrendingDown } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { filterByRange } from './ReportFilters';
import { ENGAGE_SOURCE_OPTIONS } from '../../../utils/engageSourceLinks';


const FUNNEL_ORDER = [
  { key: 'new',                label: 'New' },
  { key: 'contacted',          label: 'Contacted' },
  { key: 'interested',         label: 'Interested' },
  { key: 'appointment_booked', label: 'Appt. Booked' },
  { key: 'consulted',          label: 'Consulted' },
  { key: 'converted',          label: 'Converted (Registered)' },
];

const FUNNEL_COLOR = ['from-blue-500', 'from-cyan-500', 'from-purple-500', 'from-teal-500', 'from-amber-500', 'from-green-500'];


export default function ConversionAnalyticsReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/enquiries/');
        setEnquiries(resp?.results || resp?.data?.results || []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  // Cohort funnel: enquiries who have reached at least stage X
  // We treat status as terminal — count enquiries whose stage is at-or-past each funnel step
  const STAGE_ORDER_MAP = { new: 0, contacted: 1, interested: 2, appointment_booked: 3, consulted: 4, under_treatment: 4, follow_up_required: 4, converted: 5, dropped: -1, lost: -1 };

  const filtered = useMemo(() => {
    let f = filterByRange(enquiries, preset, customFrom, customTo, 'created_date');
    if (sourceFilter !== 'all') f = f.filter(e => e.source === sourceFilter);
    return f;
  }, [enquiries, preset, customFrom, customTo, sourceFilter]);

  const funnel = useMemo(() => {
    return FUNNEL_ORDER.map((stage, idx) => {
      const stageIdx = idx;
      const count = filtered.filter(e => (STAGE_ORDER_MAP[e.status] ?? -1) >= stageIdx).length;
      return { ...stage, count };
    });
  }, [filtered]);

  const sourceOptions = useMemo(() => [
    { value: 'all', label: 'All sources' },
    ...ENGAGE_SOURCE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
  ], []);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-sm">
          <Gauge className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Conversion Analytics</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Cohort funnel drop-off across stages</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[{ label: 'Source', value: sourceFilter, onChange: setSourceFilter, options: sourceOptions }]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div> : (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 space-y-3">
          {funnel.map((step, i) => {
            const top = funnel[0].count;
            const prev = i > 0 ? funnel[i - 1].count : step.count;
            const pctOfTop = top ? Math.round(step.count / top * 100) : 0;
            const dropOff = prev - step.count;
            const dropOffPct = prev ? Math.round((dropOff / prev) * 100) : 0;
            return (
              <div key={step.key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{step.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{step.count}</span>
                    <span className="text-[11px] text-gray-500">{pctOfTop}% of top</span>
                  </div>
                </div>
                <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                  <div className={cn('h-full bg-gradient-to-r to-blue-600 rounded-lg transition-all', FUNNEL_COLOR[i])}
                       style={{ width: `${pctOfTop}%` }} />
                </div>
                {i > 0 && dropOff > 0 && (
                  <div className="flex items-center gap-1 mt-0.5 text-[11px] text-red-500">
                    <TrendingDown className="w-3 h-3" />
                    <span>Dropped {dropOff} ({dropOffPct}% of prev)</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
